import React from 'react';
import { Target, Eye, Heart, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import CTABanner from './CTABanner';

const About = () => {
  return (
    <section id="nosotros" className="py-24 bg-white relative overflow-hidden">
      {/* Building watermark background */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none">
        <svg className="absolute left-0 bottom-0 h-3/4" width="400" viewBox="0 0 400 600" xmlns="http://www.w3.org/2000/svg">
          <rect x="50" y="150" width="100" height="450" fill="#082E5C"/>
          <rect x="180" y="100" width="120" height="500" fill="#082E5C"/>
          <rect x="60" y="170" width="20" height="25" fill="#00D4D8"/>
          <rect x="90" y="170" width="20" height="25" fill="#00D4D8"/>
          <rect x="120" y="170" width="20" height="25" fill="#00D4D8"/>
        </svg>
        <svg className="absolute right-0 bottom-0 h-3/4" width="400" viewBox="0 0 400 600" xmlns="http://www.w3.org/2000/svg">
          <rect x="250" y="200" width="90" height="400" fill="#082E5C"/>
          <rect x="100" y="120" width="130" height="480" fill="#082E5C"/>
          <rect x="260" y="220" width="18" height="22" fill="#FFDA0C"/>
          <rect x="290" y="220" width="18" height="22" fill="#FFDA0C"/>
          <rect x="318" y="220" width="18" height="22" fill="#FFDA0C"/>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#082E5C] mb-4">
            Sobre Nosotros
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Somos pioneros en la transformación digital de la administración de condominios en Chile
          </p>
        </div>

        {/* Company Description */}
        <div className="mb-16 max-w-4xl mx-auto">
          <Card className="border-2 border-[#00D4D8]/20 shadow-lg">
            <CardContent className="pt-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong className="text-[#082E5C]">Tu Condominio Digital</strong> nace de la necesidad de modernizar y optimizar la administración de comunidades en Chile. Nos diferenciamos por ser 100% digitales, integrando inteligencia artificial, automatización y tecnología de punta en cada proceso.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Combinamos años de experiencia en administración de propiedades con las más avanzadas soluciones tecnológicas, creando una experiencia única donde la eficiencia, transparencia y comodidad se encuentran en un solo lugar.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Mission, Vision, Values */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Misión */}
          <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-[#00D4D8] bg-white">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-[#00D4D8] to-[#082E5C] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Target className="text-white" size={28} />
              </div>
              <CardTitle className="text-[#082E5C]">Misión</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Revolucionar la administración de condominios mediante tecnología innovadora, entregando soluciones eficientes, transparentes y accesibles que mejoren la calidad de vida de las comunidades.
              </p>
            </CardContent>
          </Card>

          {/* Visión */}
          <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-[#00D4D8] bg-white">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-[#00D4D8] to-[#082E5C] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Eye className="text-white" size={28} />
              </div>
              <CardTitle className="text-[#082E5C]">Visión</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Ser la administradora líder en Chile, reconocida por transformar la industria con soluciones 100% digitales que establecen el estándar de excelencia en gestión de propiedades.
              </p>
            </CardContent>
          </Card>

          {/* Valores */}
          <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-[#FFDA0C] bg-white">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-[#FFDA0C] to-[#082E5C] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Heart className="text-white" size={28} />
              </div>
              <CardTitle className="text-[#082E5C]">Valores</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-gray-600 space-y-2">
                <li>• Innovación constante</li>
                <li>• Transparencia total</li>
                <li>• Compromiso con el cliente</li>
                <li>• Excelencia en servicio</li>
              </ul>
            </CardContent>
          </Card>

          {/* Diferenciador */}
          <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-[#FFDA0C] bg-white">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-[#FFDA0C] to-[#082E5C] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Award className="text-white" size={28} />
              </div>
              <CardTitle className="text-[#082E5C]">Nuestro Plus</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Somos la única administradora 100% digital en Chile. Cada proceso, desde el control de accesos hasta la gestión financiera, está potenciado por IA y automatización.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Why Choose Us */}
        <div className="mb-12 bg-gradient-to-br from-[#082E5C] to-[#0a3a6e] rounded-3xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">¿Por qué elegirnos?</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FFDA0C] mb-2">100%</div>
              <div className="text-lg">Plataforma Digital</div>
              <p className="text-sm text-gray-300 mt-2">Sin papeleos ni trámites presenciales</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FFDA0C] mb-2">24/7</div>
              <div className="text-lg">Acceso y Soporte</div>
              <p className="text-sm text-gray-300 mt-2">Control total desde cualquier lugar</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FFDA0C] mb-2">IA</div>
              <div className="text-lg">Inteligencia Artificial</div>
              <p className="text-sm text-gray-300 mt-2">Automatización inteligente de procesos</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <CTABanner variant="compact" />
      </div>
    </section>
  );
};

export default About;
